import { Check, FileText } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { QUESTION_TEMPLATES, TEMPLATE_CATEGORIES, type QuestionTemplate } from 'components/dueDiligence/templates';

interface WizardStep1Props {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
}

export function WizardStep1TemplateSelect({ selectedTemplateId, onSelectTemplate }: WizardStep1Props) {
  const handleSelectTemplate = (templateId: string | null) => {
    if (selectedTemplateId === templateId) {
      onSelectTemplate(null);
    } else {
      onSelectTemplate(templateId);
    }
  };

  const templatesByCategory = QUESTION_TEMPLATES.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, QuestionTemplate[]>);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Wat wil je controleren?</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Kies een sjabloon om snel te starten, of begin met een lege vraag.
        </p>
      </div>

      <div className="space-y-6">
        {/* Empty question card */}
        <Card
          className={`
            p-4 cursor-pointer transition-all hover:shadow-md
            ${selectedTemplateId === null ? 'ring-2 ring-primary' : 'hover:border-primary/50'}
          `}
          onClick={() => handleSelectTemplate(null)}
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-muted-foreground" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-foreground">Lege vraag</h4>
                {selectedTemplateId === null && (
                  <Check weight="bold" className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Begin met een volledig lege vraag en configureer alles zelf.
              </p>
            </div>
          </div>
        </Card>

        {/* Template cards grouped by category */}
        {Object.entries(templatesByCategory).map(([categoryId, templates]) => {
          const category = TEMPLATE_CATEGORIES.find((c) => c.id === categoryId);
          if (!category) return null;

          return (
            <div key={categoryId} className="space-y-3">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-foreground">{category.label}</h4>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.map((template) => (
                  <Card
                    key={template.id}
                    className={`
                      p-4 cursor-pointer transition-all hover:shadow-md
                      ${selectedTemplateId === template.id ? 'ring-2 ring-primary' : 'hover:border-primary/50'}
                    `}
                    onClick={() => handleSelectTemplate(template.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`
                          w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                          ${template.id.startsWith('status') ? 'bg-moss-100 text-moss-700' : ''}
                          ${template.id.startsWith('select') ? 'bg-slate-100 text-slate-700' : ''}
                          ${template.id.startsWith('document') ? 'bg-sky-100 text-sky-700' : ''}
                          ${template.id.startsWith('extraction') ? 'bg-violet-100 text-violet-700' : ''}
                        `}
                      >
                        {/* Icon placeholder - templates use string icon names */}
                        <div className="w-5 h-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h5 className="font-medium text-foreground text-sm">{template.name}</h5>
                          {selectedTemplateId === template.id && (
                            <Check weight="bold" className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {template.description}
                        </p>
                        <div className="mt-2">
                          <Badge variant="outline" className="text-xs">
                            {category.label}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
